<script setup lang="ts">
    // @ts-expect-error - no types for autosize
    import autosize from 'autosize'
    import { computed, onBeforeUnmount, ref, useTemplateRef, watch } from 'vue'

    const modelValue = defineModel<string>()
    const emit = defineEmits<{
        submit: []
        'stop-generation': []
    }>()

    const props = defineProps<{
        placeholder?: string
        dismissableNotice?: string
        status?: 'submitted' | 'streaming' | 'ready' | 'error'
    }>()

    const isDismissableNoticeVisible = ref<boolean>(true)
    const closeDismissableNotice = () => {
        isDismissableNoticeVisible.value = false
    }

    const textareaEl = useTemplateRef<HTMLTextAreaElement>('textareaEl')
    watch(textareaEl, (el) => {
        if (el) {
            autosize(el)
        }
    })

    onBeforeUnmount(() => {
        if (textareaEl.value) {
            autosize.destroy(textareaEl.value)
        }
    })

    const isStreaming = computed(() => {
        return props.status === 'streaming'
    })
    const isSubmitted = computed(() => {
        return props.status === 'submitted'
    })

    const isSubmitEnabled = computed(() => {
        if (isStreaming.value || isSubmitted.value) {
            return false
        }
        if (!modelValue.value) {
            return false
        }
        return modelValue.value.trim().length > 0
    })
    const handleSubmit = () => {
        if (isSubmitEnabled.value) {
            emit('submit')
            modelValue.value = ''
            nextTick(() => {
                if (textareaEl.value) {
                    autosize.update(textareaEl.value)
                }
            })
        }
    }
</script>

<template>
    <div class="pk-chat">
        <div
            v-if="dismissableNotice && isDismissableNoticeVisible"
            class="pk-chat-dismissable-notice">
            <span>{{ dismissableNotice }}</span>
            <VvButton
                v-if="dismissableNotice"
                :title="$t('action.close')"
                modifiers="action-quiet"
                icon="ri:close-line"
                class="w-18 h-18"
                @click="closeDismissableNotice" />
        </div>
        <div class="pk-chat-input">
            <textarea
                ref="textareaEl"
                v-model="modelValue"
                class="pk-chat-input__textarea"
                name="message"
                rows="1"
                :placeholder="placeholder"
                @keydown.enter.prevent="handleSubmit"></textarea>

            <button
                v-if="isStreaming"
                class="pk-chat-input__button"
                type="button"
                :title="$t('action.stop')"
                @click="emit('stop-generation')">
                <VvIcon
                    name="ri:stop-circle-line"
                    class="pk-chat-input__icon" />
            </button>
            <button
                v-else
                class="pk-chat-input__button"
                type="button"
                :title="
                    isSubmitEnabled ? $t('action.submit') : $t('label.disabled')
                "
                :disabled="!isSubmitEnabled"
                @click.stop="handleSubmit">
                <VvIcon
                    :name="
                        isSubmitted
                            ? 'line-md:loading-loop'
                            : isSubmitEnabled
                              ? 'ri:send-ins-fill'
                              : 'ri:send-ins-line'
                    "
                    class="pk-chat-input__icon" />
            </button>
        </div>
    </div>
</template>

<style lang="scss">
    .pk-chat {
        display: flex;
        flex-direction: column;
        margin-inline: var(--spacing-sm);
        margin-bottom: var(--spacing-sm);

        &-dismissable-notice {
            position: relative;
            padding: var(--spacing-sm);
            display: flex;
            align-items: flex-start;
            justify-content: space-between;
            gap: var(--spacing-sm);
            background-color: var(--color-surface-1);
            border-top-left-radius: var(--rounded-xl);
            border-top-right-radius: var(--rounded-xl);
            transition-property: var(--transition-property-all);
            transition-duration: var(--duration-300);
            transition-timing-function: var(--ease-in-out);
            font-size: var(--text-14);
            color: var(--color-word-2);

            &::after {
                content: '';
                display: block;
                position: absolute;
                left: 0;
                right: 0;
                bottom: calc(-1 * var(--spacing-12));
                height: var(--spacing-12);
                background: var(--color-surface-1);
                z-index: 1;
            }
        }

        &-input {
            display: flex;
            z-index: 1;
            align-items: center;
            border-radius: var(--rounded-xl);
            border: 1px solid var(--color-surface-3);
            background-color: var(--color-surface);
            padding: var(--spacing-sm);
            box-shadow: var(--shadow-lg);
            font-size: var(--text-14);
            gap: var(--spacing-xs);

            &__textarea {
                display: block;
                flex: 1;
                border: none;
                outline: none;
                resize: none;
                color: var(--color-word-1);
                background-color: var(--color-surface);
                overflow: hidden;

                &::placeholder {
                    color: var(--color-word-4);
                }
            }

            &__button {
                background-color: transparent;
                border: none;
                cursor: pointer;
                color: var(--color-word-1);
                padding: var(--spacing-4);
                flex-shrink: 0;
                transition: var(--transition-colors);
                border-radius: var(--rounded);

                &:hover {
                    background-color: var(--color-surface-1);
                }

                &:focus {
                    outline: none;
                    box-shadow: 0 0 0 1px var(--color-word-1);
                }

                &:disabled {
                    cursor: not-allowed;
                }
            }

            &__icon {
                display: block;
                width: var(--spacing-20);
                height: var(--spacing-20);
            }
        }
    }
</style>
